import React, { useState, useEffect, useCallback } from 'react';
import { Button } from 'antd';
import { DisconnectOutlined, SyncOutlined } from '@ant-design/icons';

const OfflineIndicator = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showOfflineAlert, setShowOfflineAlert] = useState(false);
  const [isChecking, setIsChecking] = useState(false);

  // Enhanced connectivity check
  const checkConnectivity = useCallback(async () => {
    setIsChecking(true);
    try {
      // First check basic navigator.onLine
      if (!navigator.onLine) {
        throw new Error('Navigator offline');
      }

      // Try to fetch a small resource to verify actual connectivity
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      
      // Check internet connectivity with a reliable DNS request
      await fetch('https://dns.google/resolve?name=google.com&type=A', {
        method: 'GET',
        cache: 'no-cache',
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      setIsOnline(true);
      setShowOfflineAlert(false);
    } catch (error) {
      setIsOnline(false);
      setShowOfflineAlert(true);
    } finally {
      setIsChecking(false);
    }
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      checkConnectivity();
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowOfflineAlert(true);
    };

    // Periodic connectivity check
    const intervalId = setInterval(() => {
      if (!navigator.onLine || !isOnline) {
        checkConnectivity();
      }
    }, 30000); // Check every 30 seconds

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial check
    checkConnectivity();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(intervalId);
    };
  }, [checkConnectivity, isOnline]);

  const handleRetry = () => {
    checkConnectivity();
  };

  if (!showOfflineAlert) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        background: 'linear-gradient(135deg, #ff6b6b, #ee5a24)',
        color: 'white',
        padding: '8px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        fontSize: '14px',
        boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.15)',
        backdropFilter: 'blur(10px)'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <DisconnectOutlined style={{ fontSize: '16px' }} />
        <span>Sem conexío com a internet</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <Button
           size="small"
           type="text"
           icon={<SyncOutlined spin={isChecking} />}
           onClick={handleRetry}
           loading={isChecking}
           disabled={isChecking}
           style={{
             color: 'white',
             border: '1px solid rgba(255, 255, 255, 0.3)',
             background: 'rgba(255, 255, 255, 0.1)'
           }}
         >
           {isChecking ? 'Verificando...' : 'Tentar novamente'}
         </Button>
        <Button
          size="small"
          type="text"
          onClick={() => setShowOfflineAlert(false)}
          style={{
            color: 'white',
            minWidth: 'auto',
            padding: '4px 8px'
          }}
        >
          ×
        </Button>
      </div>
    </div>
  );
};

export default OfflineIndicator;